import { Fragment } from 'react';

import { Combobox, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

import {
  CategoryGroupsProps,
  ComboboxInputProps,
  ComboboxOptionItemProps,
  DomainGroupsProps,
  LayerHeaderProps,
  LayoutProps,
  OptionsListProps,
  SelectedTagProps,
  TooltipProps,
} from './types';
import { isOptionSelected } from './utils';

export function ComboboxInput({
  inputClassName,
  className,
  placeholder,
  setQuery,
  displayValue,
  selectedValues,
  maxSelections,
}: ComboboxInputProps) {
  return (
    <div className="relative">
      <Combobox.Input
        className={clsx(inputClassName, className, 'w-full')}
        displayValue={displayValue}
        placeholder={selectedValues?.length ? '' : placeholder}
        onChange={(event) => setQuery(event.target.value)}
      />
      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
        <ChevronUpDownIcon
          aria-hidden="true"
          className="h-5 w-5 text-gray-400"
        />
      </Combobox.Button>
      {maxSelections !== undefined && selectedValues?.length > 0 && (
        <div className="absolute right-8 top-1/2 -translate-y-1/2 text-xs text-gray-400">
          {selectedValues.length} /{' '}
          {maxSelections === Infinity ? '∞' : maxSelections}
        </div>
      )}
    </div>
  );
}

export function TooltipDisplay({ tooltip }: TooltipProps) {
  return (
    <div className="absolute left-0 right-0 z-50 mt-2 rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-600 shadow-md sm:right-auto sm:w-auto sm:p-2">
      {tooltip}
    </div>
  );
}

export function EmptyResults() {
  return (
    <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
      Nothing found.
    </div>
  );
}

export function SelectedTag({ item, onRemove, disabled }: SelectedTagProps) {
  if (!item?.value) return null;

  return (
    <div className="inline-flex max-w-full items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-800 sm:text-lg">
      <span className="min-w-0 flex-1 leading-6">{item.name}</span>
      {!disabled && (
        <button
          className="-m-0.5 flex-shrink-0 p-0.5 text-opacity-80 hover:text-opacity-100 focus:outline-none"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(item);
          }}
        >
          <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      )}
    </div>
  );
}

export function LayerHeader({ label }: LayerHeaderProps) {
  if (label === 'Values') return null;
  return (
    <p className="pointer-events-none sticky top-0 z-30 mb-1 rounded-sm bg-blue-100 px-2 py-1 text-base font-bold text-blue-800 shadow-sm sm:text-lg">
      {label}
    </p>
  );
}

export function ComboboxOptionItem({
  option,
  setShowTooltip,
  multiSelect,
  isSelected,
  disabled,
  onOptionToggle,
}: ComboboxOptionItemProps) {
  if (!option?.value) return null;

  const handleClick = (e: React.MouseEvent) => {
    setShowTooltip(false);
    if (isSelected && onOptionToggle) {
      e.preventDefault();
      e.stopPropagation();
      onOptionToggle(option);
    }
  };

  return (
    <Combobox.Option
      key={`option-${String(option.value)}`}
      disabled={disabled}
      value={option}
      className={({ active }) =>
        clsx(
          'relative flex min-h-[44px] cursor-default select-none items-center py-3 pl-10 pr-4 sm:py-2',
          {
            'bg-blue-600 text-white': active && !disabled,
            'bg-gray-50': !active && !disabled,
            'cursor-not-allowed bg-gray-100 text-gray-500 opacity-50': disabled,
          },
        )
      }
      onClick={handleClick}
    >
      {({ selected, active }) => {
        const isItemSelected = multiSelect ? isSelected : selected;
        return (
          <>
            <span
              className={clsx('block truncate text-base sm:text-lg', {
                'font-medium': isItemSelected,
                'font-normal': !isItemSelected,
              })}
            >
              {option.name}
            </span>
            {isItemSelected && (
              <span
                className={clsx(
                  'absolute inset-y-0 left-0 flex items-center pl-3',
                  active ? 'text-white' : 'text-blue-600',
                )}
              >
                <CheckIcon aria-hidden="true" className="h-5 w-5" />
              </span>
            )}
          </>
        );
      }}
    </Combobox.Option>
  );
}

export function OptionsList({
  options,
  setShowTooltip,
  multiSelect,
  selectedValues,
  canAddMore,
  maximumDomainItems,
  domainCounts,
  onOptionToggle,
}: OptionsListProps) {
  if (!options?.length) return null;

  return (
    <>
      {options
        .filter((option) => option?.value !== undefined)
        .map((option) => {
          const isSelected = isOptionSelected(option, selectedValues);
          const isDomainLimitReached =
            maximumDomainItems &&
            option.domain &&
            domainCounts?.[option.domain] >= maximumDomainItems;
          const isDisabled =
            (!canAddMore || isDomainLimitReached) && !isSelected;

          return (
            <ComboboxOptionItem
              key={`option-${String(option.value)}`}
              disabled={isDisabled}
              isSelected={isSelected}
              multiSelect={multiSelect}
              option={option}
              setShowTooltip={setShowTooltip}
              onOptionToggle={onOptionToggle}
            />
          );
        })}
    </>
  );
}

export function CategoryGroups({
  categories,
  layerName,
  domainName,
  setShowTooltip,
  multiSelect,
  selectedValues,
  canAddMore,
  maximumDomainItems,
  domainCounts,
  onOptionToggle,
}: CategoryGroupsProps) {
  if (!categories) return null;

  const categoryKeys = Object.keys(categories);

  // If there's only one category and it's "default", don't show the category header
  const showCategoryHeader = !(
    categoryKeys.length === 1 && categoryKeys[0] === 'default'
  );

  return (
    <>
      {categoryKeys.map((categoryName) => {
        if (!categories[categoryName]?.length) return null;

        return (
          <div
            key={`${layerName}-${domainName}-${categoryName}`}
            className={clsx(showCategoryHeader && 'mb-1', 'bg-gray-50')}
          >
            {showCategoryHeader && (
              <p className="pointer-events-none sticky top-9 z-10 rounded-lg border border-gray-200 bg-white/90 px-2 py-1 text-lg italic text-black backdrop-blur-sm">
                {categoryName}
              </p>
            )}
            <OptionsList
              canAddMore={canAddMore}
              domainCounts={domainCounts}
              maximumDomainItems={maximumDomainItems}
              multiSelect={multiSelect}
              options={categories[categoryName]}
              selectedValues={selectedValues}
              setShowTooltip={setShowTooltip}
              onOptionToggle={onOptionToggle}
            />
          </div>
        );
      })}
    </>
  );
}

export function DomainGroups({
  domains,
  layerName,
  setShowTooltip,
  multiSelect,
  selectedValues,
  canAddMore,
  maximumDomainItems,
  domainCounts,
  onOptionToggle,
}: DomainGroupsProps) {
  if (!domains) return null;

  const domainKeys = Object.keys(domains);

  // If there's only one domain and it's "default", don't show the domain header
  const showDomainHeader = !(
    domainKeys.length === 1 && domainKeys[0] === 'default'
  );

  return (
    <>
      {domainKeys.map((domainName) => {
        if (!domains[domainName]) return null;

        const domainCount = domainCounts?.[domainName] || 0;
        const canAddToDomain =
          !maximumDomainItems || domainCount < maximumDomainItems;

        return (
          <div
            key={`${layerName}-${domainName}`}
            className={clsx(showDomainHeader && 'mb-2 pl-2')}
          >
            {showDomainHeader && (
              <p
                className={clsx(
                  'sticky top-0 z-20 bg-gray-100 px-2 text-lg font-semibold text-gray-800 shadow-sm',
                )}
              >
                {layerName === 'Values' && domainName}
                {maximumDomainItems && (
                  <span className="ml-2 text-opacity-75">
                    {domainCount} / {maximumDomainItems}
                  </span>
                )}
              </p>
            )}
            <CategoryGroups
              canAddMore={canAddMore && canAddToDomain}
              categories={domains[domainName]}
              domainCounts={domainCounts}
              domainName={domainName}
              layerName={layerName}
              maximumDomainItems={maximumDomainItems}
              multiSelect={multiSelect}
              selectedValues={selectedValues}
              setShowTooltip={setShowTooltip}
              onOptionToggle={onOptionToggle}
            />
          </div>
        );
      })}
    </>
  );
}

export function TwoColumnLayout({
  groupedOptions,
  setShowTooltip,
  multiSelect,
  selectedValues,
  canAddMore,
  maximumDomainItems,
  domainCounts,
  onOptionToggle,
}: LayoutProps) {
  if (!groupedOptions) return null;

  const layerKeys = Object.keys(groupedOptions);

  // If there's only one layer and it's named 'Items', we can skip the layer header
  const showLayerHeaders = !(
    layerKeys.length === 1 && layerKeys[0] === 'Items'
  );

  return (
    <div className="grid max-h-[19rem] grid-cols-2 gap-1 overflow-auto">
      {layerKeys.map((layerName) => (
        <div key={layerName} className="relative">
          {showLayerHeaders && <LayerHeader label={layerName} />}
          <DomainGroups
            canAddMore={canAddMore}
            domainCounts={domainCounts}
            domains={groupedOptions[layerName]}
            layerName={layerName}
            maximumDomainItems={maximumDomainItems}
            multiSelect={multiSelect}
            selectedValues={selectedValues}
            setShowTooltip={setShowTooltip}
            onOptionToggle={onOptionToggle}
          />
        </div>
      ))}
    </div>
  );
}

export function SingleColumnLayout({
  groupedOptions,
  setShowTooltip,
  multiSelect,
  selectedValues,
  canAddMore,
  maximumDomainItems,
  domainCounts,
  onOptionToggle,
}: LayoutProps) {
  if (!groupedOptions) return null;

  const layerKeys = Object.keys(groupedOptions);

  // If there's only one layer and it's named 'Items', we can skip the layer header
  const showLayerHeaders = !(
    layerKeys.length === 1 && layerKeys[0] === 'Items'
  );

  return (
    <div className="max-h-[19rem] overflow-auto">
      {layerKeys.map((layerName) => (
        <div key={layerName} className="relative">
          {showLayerHeaders && <LayerHeader label={layerName} />}
          <DomainGroups
            canAddMore={canAddMore}
            domainCounts={domainCounts}
            domains={groupedOptions[layerName]}
            layerName={layerName}
            maximumDomainItems={maximumDomainItems}
            multiSelect={multiSelect}
            selectedValues={selectedValues}
            setShowTooltip={setShowTooltip}
            onOptionToggle={onOptionToggle}
          />
        </div>
      ))}
    </div>
  );
}

export function ComboboxTransition({
  children,
  setQuery,
}: {
  children: React.ReactNode;
  setQuery: (query: string) => void;
}) {
  return (
    <Transition
      afterLeave={() => setQuery('')}
      as={Fragment}
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      {children}
    </Transition>
  );
}
